import { AccountsService } from 'base/accounts_service';
import { AccountStore } from 'base/account_store';
import { AnalyticsService } from 'base/analytics';
import { keyOfObject, UnreachableError } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { useDocumentTitle } from 'base/ui/document_title';
import { Inline } from 'base/ui/layout';
import { ToastLayer, ToastQueue } from 'base/ui/toast';
import {
  AccessLevel,
  BlocksService,
  ProjectOwner,
} from 'blocks/blocks_service';
import { currentUrl } from 'blocks/current_url';
import { StyleguidePages } from 'blocks/editor/styleguide/pages';
import { observer } from 'mobx-react-lite';
import { computedFn } from 'mobx-utils';
import React from 'react';
import { shortcut, useKeyboardShortcuts } from '../keyboard_shortcuts';
import { HttpLLMProxyService } from '../llm_proxy_service';
import { Assistant, createAssistant } from './ai/assistant';
import {
  Canvas,
  CanvasViewport,
  useDisableSwipeBack,
  ViewportState,
} from './canvas';
import { CommentingExport, createCommenting } from './commenting';
import { initGlobalDesignStore } from './design/design_store';
import { DesignPages } from './design/pages';
import { ProjectEditingActions } from './editing_actions/project';
import styles from './editor.css';
import {
  EditorMode,
  editorModeStore,
  isWireframeOrDesign,
} from './editor_mode';
import { ProjectLoadErrorDialog } from './error_dialogs';
import {
  handleEscapeShortcut,
  handleSelectParentShortcut,
} from './escape_shortcut';
import { ExperimentPrompt, EXPRIMENT_QUERY_PARAM } from './experiment_prompt';
import { Header, useUndoShortcuts } from './header';
import { AppLoadingCurtain, WireframeLoadingCurtain } from './loading_curtain';
import { MaybeOnboardingDialog } from './onboarding/dialog';
import {
  globalSelectionHoverRegistry,
  SelectionFrames,
} from './selection/frame';
import { globalSelectionsMeasurer } from './selection/measurer';
import { useClickOrHoverToSelect } from './selection/selectable';
import { SelectionState } from './selection/states';
import { SelectionStore, SelectionStoreImpl } from './selection/store';
import {
  SelectionActionsDialogs,
  SelectionActionsImpl,
  useSelectionActionShortcuts,
} from './selection_actions';
import { startSession } from './session/load_session';
import { ProjectSession } from './session/session';
import { createSidebar } from './sidebar';
import { SidebarNavigation } from './sidebar/routes';
import { SitemapSpaces } from './sitemap/spaces';
import { SpaceSelector } from './space_selector';
import { initGlobalConceptsStore } from './styleguide/concept_store';
import { MaybeSurveyForm } from './survey_form';
import { UndoRedoControls } from './undo_redo_controls';
import { WireframePages } from './wireframe/pages';
import { ZoomAndBreakpointControls } from './zoom_and_breakpoint_controls';

function isModeAllowed(session: ProjectSession, mode: EditorMode) {
  if (mode === EditorMode.Design && !session.flags.get('enableDesignView')) {
    return false;
  }
  return true;
}

type ProjectEditorProps = {
  session: ProjectSession;
  toastQueue: ToastQueue;
  accountsService: AccountsService;
  blocksService: BlocksService;
  llm: HttpLLMProxyService;
  library: LibraryCache;
};
const ProjectEditor = observer(function _ProjectEditor({
  session,
  toastQueue,
  accountsService,
  blocksService,
  llm,
  library,
}: ProjectEditorProps) {
  const { project, undo, decorations, accessLevel } = session;
  const [
    {
      analytics,
      viewport,
      selection,
      sidebar,
      commenting,
      projectEditingDangerous,
      assistantLayer,
      assistant,
    },
  ] = React.useState(() => {
    const analytics = session.analytics.withContext(() => ({
      editorMode: editorModeStore.get(),
    }));
    const viewport = new CanvasViewport(
      globalSelectionsMeasurer.onCanvasTransformOrSizeChange,
      session.flags.get('enableCanvasCursorOptimization'),
    );
    const selection = new SelectionStoreImpl(
      project,
      () => editorModeStore.get(),
      viewport,
      session,
      decorations,
      accessLevel >= AccessLevel.COMMENTER,
    );
    const [assistant, assistantLayer] = createAssistant(
      selection,
      session,
      undo,
      llm,
      library,
      session.flags,
      toastQueue,
    );
    const commenting = createCommenting(
      session,
      blocksService,
      selection,
      toastQueue,
    );
    const sidebar = createSidebar(
      assistant,
      analytics,
      blocksService,
      library,
      toastQueue,
      selection,
      session,
      commenting,
      session.flags,
    );
    const projectEditingDangerous = new ProjectEditingActions(
      selection,
      session,
      toastQueue,
      sidebar.navigation,
      assistant,
      analytics,
      library,
    );
    return {
      toastQueue,
      analytics,
      viewport,
      selection,
      sidebar,
      commenting,
      projectEditingDangerous,
      assistantLayer,
      assistant,
    };
  });
  // Fallback to rendering the sitemap if the user is trying to navigate
  // to a page that is locked behind a flag or to a non existant page.
  const currentMode = editorModeStore.get();
  if (!isModeAllowed(session, currentMode)) {
    editorModeStore.set(EditorMode.Sitemap);
  }

  const projectEditing =
    !session.locked && accessLevel >= AccessLevel.OWNER
      ? projectEditingDangerous
      : undefined;
  const selectionActions = React.useMemo(
    () =>
      new SelectionActionsImpl(
        selection,
        toastQueue,
        projectEditing,
        analytics,
        library,
        session,
      ),
    [selection, toastQueue, projectEditing, analytics, library, session],
  );

  React.useEffect(() => {
    const handler = session.beforeUnloadHandler;
    if (!handler) return;
    const opts = { capture: true };
    window.addEventListener('beforeunload', handler, opts);
    return () => window.removeEventListener('beforeunload', handler, opts);
  }, [session.beforeUnloadHandler]);

  useKeyboardShortcuts(true, {
    [shortcut('Escape')]: () =>
      !commenting.maybeHandleEscape() &&
      handleEscapeShortcut(selection, sidebar.navigation, library),
    [shortcut('v')]: () =>
      !commenting.maybeHandleEscape() &&
      handleEscapeShortcut(selection, sidebar.navigation, library),
    [shortcut('\\')]: () => handleSelectParentShortcut(selection, library),
    [shortcut('s', { cmd: true })]: session.saveShortcutHandler,
  });
  useSelectionActionShortcuts(selectionActions);
  useUndoShortcuts(undo, toastQueue);

  const viewportKey = getViewportSaveKey(currentMode, selection);
  const { restoreViewport } = useViewportSaveRestore(viewport, viewportKey);

  useDisableSwipeBack();

  // We automatically close the edit panel if generating copy in the wireframe
  // view and swap to the sitemap view.
  const onSwitchMode = (newMode: EditorMode) => {
    if (isWireframeOrDesign(newMode)) return;
    const sel = selection.get();
    const pageOrSection = sel.page || sel.section;
    if (!pageOrSection) return;
    const isGenerating = assistant.taskLog.affectedByPending.has(pageOrSection);
    if (isGenerating) sidebar.navigation.close();
  };
  const title = `${project.name || 'Untitled Project'} - Relume`;
  useDocumentTitle(title);

  const projectOwner = useProjectOwner(
    blocksService,
    session.projectToken,
    toastQueue,
  );

  let editorToRender: JSX.Element;
  switch (currentMode) {
    case EditorMode.Styleguide:
      editorToRender = (
        <StyleguidePages
          accountsService={accountsService}
          selection={selection}
          selectionActions={selectionActions}
          analytics={analytics}
          session={session}
          editing={projectEditing}
          library={library}
          toastQueue={toastQueue}
          blocksService={blocksService}
          wireframeNavigation={sidebar.navigation}
          projectOwner={projectOwner}
        />
      );
      break;
    case EditorMode.Sitemap:
    case EditorMode.Wireframe:
    case EditorMode.Design:
      editorToRender = (
        <CanvasView
          selection={selection}
          selectionActions={selectionActions}
          analytics={analytics}
          session={session}
          editing={projectEditing}
          viewport={viewport}
          library={library}
          sidebar={sidebar}
          commenting={commenting}
          blocksService={blocksService}
          assistant={assistant}
        />
      );
      break;
    default:
      throw new UnreachableError(currentMode);
  }

  const isExperiment = currentUrl.searchParams.has(EXPRIMENT_QUERY_PARAM);

  return (
    <>
      {isWireframeOrDesign(currentMode) ? (
        // The key is used here to force a remount of the loading curtain when
        // switching between different spaces, since rendering the new set of
        // wireframes can take a while.
        <WireframeLoadingCurtain
          key={viewportKey}
          onFinishedRendering={restoreViewport}
        >
          {editorToRender}
        </WireframeLoadingCurtain>
      ) : (
        editorToRender
      )}
      <Header
        viewport={viewport}
        project={project}
        library={library}
        selectionActions={selectionActions}
        selection={selection}
        commenting={commenting}
        flags={session.flags}
        accountsService={accountsService}
        toastQueue={toastQueue}
        session={session}
        blocksService={blocksService}
        analytics={analytics}
        onSwitchMode={onSwitchMode}
        projectOwner={projectOwner}
      />
      {accessLevel >= AccessLevel.OWNER && (
        <MaybeOnboardingDialog
          isNewProject={session.isNewProject}
          isExperiment={isExperiment}
          currentMode={currentMode}
        />
      )}
      <ExperimentPrompt
        navigation={sidebar.navigation}
        selection={selection}
        editing={projectEditing}
        assistant={assistant}
        session={session}
        isExperiment={isExperiment}
      />
      {assistantLayer}
      {/* TODO: Comment when survey is finished */}
      {accessLevel >= AccessLevel.OWNER && session.showAuthWall == null && (
        <MaybeSurveyForm />
      )}
      {session.dialogLayer}
      {projectEditing?.confirmations.currentDialog}
      <ToastLayer queue={toastQueue} bottomOffset={0} />
    </>
  );
});

const CanvasView = observer(function _CanvasEditor({
  selection,
  selectionActions,
  analytics,
  session,
  editing,
  viewport,
  library,
  sidebar,
  commenting,
  blocksService,
  assistant,
}: {
  selection: SelectionStore;
  selectionActions: SelectionActionsImpl;
  analytics: AnalyticsService;
  session: ProjectSession;
  editing: ProjectEditingActions | undefined;
  viewport: CanvasViewport;
  library: LibraryCache;
  sidebar: { Sidebar: any; navigation: SidebarNavigation };
  commenting: CommentingExport;
  blocksService: BlocksService;
  assistant: Assistant;
}) {
  const currentMode = editorModeStore.get();
  let pageToRender;

  switch (currentMode) {
    case EditorMode.Wireframe:
      pageToRender = (
        <WireframePages
          selection={selection}
          selectionActions={selectionActions}
          analytics={analytics}
          session={session}
          editing={editing}
          library={library}
        />
      );
      break;
    case EditorMode.Design:
      pageToRender = (
        <DesignPages
          selection={selection}
          selectionActions={selectionActions}
          analytics={analytics}
          session={session}
          editing={editing}
          library={library}
          blocksService={blocksService}
          assistant={assistant}
        />
      );
      break;
    case EditorMode.Sitemap:
      pageToRender = (
        <SitemapSpaces
          selection={selection}
          selectionActions={selectionActions}
          analytics={analytics}
          editing={editing}
          flags={session.flags}
        />
      );
      break;
    case EditorMode.Styleguide:
      throw new Error('Styleguide mode not supported by CanvasView');
    default:
      throw new UnreachableError(currentMode);
  }
  const [canvasRef, canvasContentRef] = useCanvasSelectionTarget(
    selection,
    sidebar.navigation,
  );

  return (
    <>
      <Canvas
        viewport={viewport}
        canvasRef={canvasRef}
        unscaledOverlays={
          <>
            <SelectionFrames
              store={selection}
              flags={session.flags}
              library={library}
            />
            {commenting.overlay}
          </>
        }
        minHeight="100vh"
      >
        <div ref={canvasContentRef}>{pageToRender}</div>
      </Canvas>
      <BottomRightToolbar>
        <SpaceSelector
          selection={selection}
          editing={editing}
          analytics={analytics}
        />
        <ZoomAndBreakpointControls viewport={viewport} analytics={analytics} />
      </BottomRightToolbar>
      <UndoRedoControls undoManager={session.undo} />
      <SelectionActionsDialogs selectionActions={selectionActions} />
      <sidebar.Sidebar editing={editing} selectionActions={selectionActions} />
    </>
  );
});

type BottomRightToolbarProps = React.PropsWithChildren;

function BottomRightToolbar({ children }: BottomRightToolbarProps) {
  return (
    <div className={styles.bottomRightToolbar}>
      <Inline spacing={8} yalign="center">
        {children}
      </Inline>
    </div>
  );
}

function useCanvasSelectionTarget(
  selection: SelectionStore,
  sidebarNavigation: SidebarNavigation,
) {
  const canvasRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const target = getDefaultSelection(selection);

  useClickOrHoverToSelect(canvasRef, selection, target, {
    onSelect: () => {
      sidebarNavigation.close();
    },
  });

  React.useEffect(() => {
    // But "canvasRef" doesn't get transformed as the user pans/zooms.
    // So we say "contentRef" is the "original selection box".  That makes
    // commenting work correctly.
    // What a big ball of mud.
    const content = contentRef.current;
    if (!content) return;
    return globalSelectionHoverRegistry.attach(target, content, 'original');
  }, [target]);

  return [canvasRef, contentRef] as const;
}

export type UseProjectOwner =
  | { loading: true }
  | { loading: false; error: unknown; data?: undefined }
  | { loading: false; data: ProjectOwner; error?: undefined };

export function useProjectOwner(
  blocksService: BlocksService,
  projectToken: string,
  toastQueue: ToastQueue,
): UseProjectOwner {
  const [result, setResult] = React.useState<UseProjectOwner>({
    loading: true,
  });

  React.useEffect(() => {
    blocksService
      .projectGetOwner(projectToken)
      .then((projectOwner) => {
        setResult({ loading: false, data: projectOwner });
      })
      .catch((error) => {
        setResult({ loading: false, error });
        toastQueue.push({
          label: `Hmm, it seems we can't load some project data. Please try again`,
        });
      });
  }, [blocksService, projectToken, toastQueue]);

  return result;
}

const getDefaultSelection = computedFn(
  (selection: SelectionStore): SelectionState => {
    const { space } = selection.get();
    // We can't clear the space from the wireframe mode selection, as that'd
    // change the user's view.
    return isWireframeOrDesign(editorModeStore.get()) &&
      space &&
      space !== 'home'
      ? { space }
      : {};
  },
);

/** Save the viewport's state when the key changes */
function useViewportSaveRestore(viewport: CanvasViewport, key: string) {
  const statesBox = React.useRef<Map<string, ViewportState>>(new Map());

  const restoreViewport = React.useCallback(() => {
    const states = statesBox.current;
    const toRestore = states.get(key);
    if (toRestore) {
      if (editorModeStore.get() === EditorMode.Styleguide) {
        viewport.zoomToFit();
      } else {
        viewport.restore(toRestore);
      }
    } else {
      viewport.zoomToFit();
    }
  }, [key, viewport]);

  React.useEffect(() => {
    if (!isWireframeOrDesign(editorModeStore.get())) {
      restoreViewport();
    }

    const states = statesBox.current;
    return () => {
      states.set(key, viewport.save());
    };
  }, [key, restoreViewport, viewport]);

  return { restoreViewport };
}

function getViewportSaveKey(
  mode: EditorMode,
  selection: SelectionStore,
): string {
  switch (mode) {
    case EditorMode.Sitemap:
      return 'sitemap';
    case EditorMode.Styleguide:
      return 'styleguide';
    case EditorMode.Wireframe:
    case EditorMode.Design:
      const { space } = selection.get();
      if (space == null || space === 'home') return mode;
      return `${mode}-${keyOfObject(space)}`;
    default:
      throw new UnreachableError(mode);
  }
}

type EditorProps = { projectToken: string; isLoggedIn: boolean };

export function installEditor(
  accountStore: AccountStore,
  toastQueue: ToastQueue,
  blocksService: BlocksService,
  accountsService: AccountsService,
  analyticsNoContext: AnalyticsService,
  llm: HttpLLMProxyService,
  {
    productionLibraryService,
    stagingLibraryService,
  }: {
    productionLibraryService: LibraryCache;
    stagingLibraryService: LibraryCache;
  },
): React.ComponentType<EditorProps> {
  function Editor({ projectToken, isLoggedIn }: EditorProps) {
    const [state, setState] = React.useState<
      | { type: 'loading' }
      | ({ type: 'loaded' } & ProjectEditorProps)
      | { type: 'error'; error: unknown }
    >({ type: 'loading' });

    const startSessionAndLoadData = React.useCallback(async () => {
      try {
        const session = await startSession(
          accountStore,
          blocksService,
          accountsService,
          analyticsNoContext,
          toastQueue,
          projectToken,
          isLoggedIn,
        );

        const library = session.flags.get('useStagingComponents')
          ? stagingLibraryService
          : productionLibraryService;

        initGlobalConceptsStore(session, blocksService, library, toastQueue);

        if (session.flags.get('enableDesignView')) {
          initGlobalDesignStore(session);
        }

        setState({
          type: 'loaded',
          session,
          toastQueue,
          blocksService,
          accountsService,
          llm: llm.withBlocksProjectId(projectTokenToId(projectToken)),
          library,
        });
      } catch (error) {
        console.error('Project load error', error);
        setState({ type: 'error', error });
      }
    }, [isLoggedIn, projectToken]);

    React.useEffect(
      () => void startSessionAndLoadData(),
      [projectToken, isLoggedIn, startSessionAndLoadData],
    );

    return (
      <AppLoadingCurtain loading={state.type === 'loading'}>
        {state.type === 'error' ? (
          <ProjectLoadErrorDialog error={state.error} />
        ) : state.type === 'loaded' ? (
          <ProjectEditor {...state} />
        ) : null}
      </AppLoadingCurtain>
    );
  }

  return Editor;
}

function projectTokenToId(projectToken: string): string {
  const match = projectToken.match(/^P([0-9]+)_/);
  return match ? match[1] : '';
}
